import { create } from 'zustand';
import { mockCurriculumPlans, mockOptionBlocks } from '../lib/mockData/curriculum';
import { mockSubjects } from '../lib/mockData/subjects';
import type { CurriculumPlan, OptionBlock, Subject } from '../types';

interface CurriculumState {
  // Subjects
  subjects: Subject[];

  // Curriculum Plans
  curriculumPlans: CurriculumPlan[];

  // Option Blocks
  optionBlocks: OptionBlock[];

  // Subject CRUD
  addSubject: (subject: Subject) => void;
  updateSubject: (id: string, subject: Partial<Subject>) => void;
  deleteSubject: (id: string) => void;
  getSubject: (id: string) => Subject | undefined;
  getSubjectsByCategory: (category: Subject['category']) => Subject[];
  getSubjectsByDepartment: (department: string) => Subject[];
  searchSubjects: (query: string) => Subject[];

  // Curriculum Plan CRUD
  addCurriculumPlan: (plan: CurriculumPlan) => void;
  updateCurriculumPlan: (id: string, plan: Partial<CurriculumPlan>) => void;
  deleteCurriculumPlan: (id: string) => void;
  getCurriculumPlan: (id: string) => CurriculumPlan | undefined;
  getCurriculumPlansByYearGroup: (yearGroupId: string) => CurriculumPlan[];
  getCurriculumPlansBySubject: (subjectId: string) => CurriculumPlan[];
  getCorePlans: () => CurriculumPlan[];
  getElectivePlans: () => CurriculumPlan[];

  // Option Block CRUD
  addOptionBlock: (block: OptionBlock) => void;
  updateOptionBlock: (id: string, block: Partial<OptionBlock>) => void;
  deleteOptionBlock: (id: string) => void;
  getOptionBlock: (id: string) => OptionBlock | undefined;
  getOptionBlocksByYearGroup: (yearGroupId: string) => OptionBlock[];

  // Utility functions
  getTotalLessonsPerWeek: (yearGroupId: string) => number;
  getSubjectTeachingHours: (subjectId: string) => number;
  validateOptionBlockConflicts: (blockId: string) => { conflicts: boolean; details: string[] };
  reset: () => void;
}

const initialState = {
  subjects: mockSubjects,
  curriculumPlans: mockCurriculumPlans,
  optionBlocks: mockOptionBlocks,
};

export const useCurriculumStore = create<CurriculumState>((set, get) => ({
  ...initialState,

  // Subject CRUD
  addSubject: (subject) =>
    set((state) => ({
      subjects: [...state.subjects, subject],
    })),

  updateSubject: (id, subject) =>
    set((state) => ({
      subjects: state.subjects.map((s) => (s.id === id ? { ...s, ...subject } : s)),
    })),

  deleteSubject: (id) =>
    set((state) => ({
      subjects: state.subjects.filter((s) => s.id !== id),
      // Also remove associated curriculum plans
      curriculumPlans: state.curriculumPlans.filter((p) => p.subjectId !== id),
    })),

  getSubject: (id) => get().subjects.find((s) => s.id === id),

  getSubjectsByCategory: (category) => get().subjects.filter((s) => s.category === category),

  getSubjectsByDepartment: (department) => get().subjects.filter((s) => s.department === department),

  searchSubjects: (query) => {
    const lowerQuery = query.toLowerCase();
    return get().subjects.filter(
      (s) =>
        s.name.toLowerCase().includes(lowerQuery) ||
        s.code.toLowerCase().includes(lowerQuery) ||
        s.examCode?.toLowerCase().includes(lowerQuery) ||
        s.department?.toLowerCase().includes(lowerQuery),
    );
  },

  // Curriculum Plan CRUD
  addCurriculumPlan: (plan) =>
    set((state) => ({
      curriculumPlans: [...state.curriculumPlans, plan],
    })),

  updateCurriculumPlan: (id, plan) =>
    set((state) => ({
      curriculumPlans: state.curriculumPlans.map((p) => (p.id === id ? { ...p, ...plan } : p)),
    })),

  deleteCurriculumPlan: (id) =>
    set((state) => ({
      curriculumPlans: state.curriculumPlans.filter((p) => p.id !== id),
    })),

  getCurriculumPlan: (id) => get().curriculumPlans.find((p) => p.id === id),

  getCurriculumPlansByYearGroup: (yearGroupId) => get().curriculumPlans.filter((p) => p.yearGroupId === yearGroupId),

  getCurriculumPlansBySubject: (subjectId) => get().curriculumPlans.filter((p) => p.subjectId === subjectId),

  getCorePlans: () => get().curriculumPlans.filter((p) => !p.constraints.isElective),

  getElectivePlans: () => get().curriculumPlans.filter((p) => p.constraints.isElective),

  // Option Block CRUD
  addOptionBlock: (block) =>
    set((state) => ({
      optionBlocks: [...state.optionBlocks, block],
    })),

  updateOptionBlock: (id, block) =>
    set((state) => ({
      optionBlocks: state.optionBlocks.map((b) => (b.id === id ? { ...b, ...block } : b)),
    })),

  deleteOptionBlock: (id) =>
    set((state) => ({
      optionBlocks: state.optionBlocks.filter((b) => b.id !== id),
      // Also update curriculum plans that reference this block
      curriculumPlans: state.curriculumPlans.map((p) =>
        p.constraints.optionBlock === id ? { ...p, constraints: { ...p.constraints, optionBlock: undefined } } : p,
      ),
    })),

  getOptionBlock: (id) => get().optionBlocks.find((b) => b.id === id),

  getOptionBlocksByYearGroup: (yearGroupId) => get().optionBlocks.filter((b) => b.yearGroupId === yearGroupId),

  // Utility functions
  getTotalLessonsPerWeek: (yearGroupId) => {
    return get()
      .curriculumPlans.filter((p) => p.yearGroupId === yearGroupId)
      .reduce((total, plan) => total + plan.lessonsPerWeek, 0);
  },

  getSubjectTeachingHours: (subjectId) => {
    return get()
      .curriculumPlans.filter((p) => p.subjectId === subjectId)
      .reduce((total, plan) => total + plan.lessonsPerWeek, 0);
  },

  validateOptionBlockConflicts: (blockId) => {
    const block = get().getOptionBlock(blockId);
    const details: string[] = [];

    if (!block) {
      return { conflicts: true, details: ['Option block not found'] };
    }

    // Check if periods are defined for simultaneous blocks
    if (block.simultaneous && (!block.periods || block.periods.length === 0)) {
      details.push('Simultaneous blocks require defined periods');
    }

    // Check for period overlaps with other blocks in same year
    const yearBlocks = get().getOptionBlocksByYearGroup(block.yearGroupId);
    const otherBlocks = yearBlocks.filter((b) => b.id !== blockId && b.simultaneous && b.periods);

    if (block.periods) {
      for (const period of block.periods) {
        for (const otherBlock of otherBlocks) {
          if (otherBlock.periods) {
            const hasOverlap = otherBlock.periods.some((p) => p.day === period.day && p.period === period.period);
            if (hasOverlap) {
              details.push(`Period conflict with ${otherBlock.name} on ${period.day} period ${period.period}`);
            }
          }
        }
      }
    }

    // Check if subjects in block have curriculum plans
    const plans = get().curriculumPlans;
    for (const subjectId of block.subjects) {
      const hasplan = plans.some((p) => p.subjectId === subjectId && p.yearGroupId === block.yearGroupId);
      if (!hasplan) {
        const subject = get().getSubject(subjectId);
        details.push(`Subject ${subject?.name || subjectId} has no curriculum plan for ${block.yearGroupId}`);
      }
    }

    return {
      conflicts: details.length > 0,
      details,
    };
  },

  reset: () => set(initialState),
}));
